import { useCallback, useState } from 'react';
import { clampDateRange, getDefaultTruthDateRange } from '../utils/profitabilityDateRange';

export interface ProfitabilityDateRange {
  startDate: string;
  endDate: string;
}

export interface UseProfitabilityDateRangeResult extends ProfitabilityDateRange {
  setStartDate: (value: string) => void;
  setEndDate: (value: string) => void;
  setDateRange: (startDate: string, endDate: string) => void;
  resetToDefault: () => void;
}

/**
 * Adjustable ISO date range for truth-layer profitability fetches.
 * Defaults to the last 7 days (inclusive of today), matching Fleet Intelligence.
 */
export function useProfitabilityDateRange(
  initial?: Partial<ProfitabilityDateRange>,
): UseProfitabilityDateRangeResult {
  const defaults = getDefaultTruthDateRange();
  const [startDate, setStartDateState] = useState(initial?.startDate ?? defaults.startDate);
  const [endDate, setEndDateState] = useState(initial?.endDate ?? defaults.endDate);

  const applyRange = useCallback((start: string, end: string) => {
    const clamped = clampDateRange(start, end);
    setStartDateState(clamped.startDate);
    setEndDateState(clamped.endDate);
  }, []);

  const setStartDate = useCallback(
    (value: string) => applyRange(value, endDate),
    [applyRange, endDate],
  );

  const setEndDate = useCallback(
    (value: string) => applyRange(startDate, value),
    [applyRange, startDate],
  );

  const resetToDefault = useCallback(() => {
    const next = getDefaultTruthDateRange();
    setStartDateState(next.startDate);
    setEndDateState(next.endDate);
  }, []);

  const setDateRange = useCallback(
    (start: string, end: string) => applyRange(start, end),
    [applyRange],
  );

  return { startDate, endDate, setStartDate, setEndDate, setDateRange, resetToDefault };
}
